import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowLeftIcon,
  HomeIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  PhotoIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { FEATURES } from '../config/phases';

// video.kind (app/models/video.py VideoKind: clip, walkthrough, upload). 6.4's pill recipe
// covers clip/walkthrough only; "upload" gets a neutral pill of its own rather than being
// silently dropped, since 20's VideoKindLiteral includes it and a P6 upload can reach this
// screen.
const KIND_PILLS = {
  clip: { label: 'Clip', className: 'bg-indigo-100 text-indigo-800' },
  walkthrough: { label: 'Walkthrough', className: 'bg-amber-100 text-amber-900' },
  upload: { label: 'Upload', className: 'bg-gray-100 text-gray-700' },
  // Anshul, 2026-09-26: a photo set is its own item in the Videos list, with its own pill
  // here exactly like every other kind rather than falling through to no pill at all.
  photos: { label: 'Photos', className: 'bg-teal-100 text-teal-800' },
};

// video.status (app/models/video.py VideoStatus). Own copy rather than an import from
// ProjectVideosPage.js: that file belongs to a different phase's file set and exports
// nothing but its default component, and 6.4 documents this exact IDLE/DONE-augmented
// recipe for the video-detail screen's own badge.
//
// READY says "Ready", not this file's former "Not started": ProjectVideosPage.js's own
// STATUS_BADGES.READY already says "Ready" and its test (ProjectVideosPage.test.js) already
// asserts that text, and a user moving from that row straight into this same video's detail
// page (they're one click apart) was seeing the identical `status` value relabelled out from
// under them. "Ready" wins the pick because it is the wording already shipped, tested and
// user-facing on the screen one click away, not because of anything about the word itself.
const STATUS_BADGES = {
  UPLOADING: { label: 'Uploading', className: 'bg-blue-100 text-blue-800' },
  READY: { label: 'Ready', className: 'text-gray-700 bg-[#F8F8FF]' },
  PROCESSING: { label: 'Processing', className: 'bg-blue-100 text-blue-800' },
  DONE: { label: 'Done', className: 'text-green-700 bg-green-100' },
  FAILED: { label: 'Failed', className: 'bg-red-100 text-red-800' },
  FAILED_UPLOAD: { label: 'Upload failed', className: 'bg-red-100 text-red-800' },
};

/** "6:52" / "1:02:05" from a seconds count. Shared with VideoOverviewPage, ReviewQueuePage
 * and FindingCard (all import it from here) rather than duplicated per file, since
 * section 6.1's own `utils/timeFormat.js` is not one of this phase's files to create. */
export function formatDuration(seconds) {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '—';
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${minutes}:${String(secs).padStart(2, '0')}`;
}

/**
 * Shared header for all three video-detail pages (19.3: "a video header strip ... and
 * tabs Overview / Review queue / Report using the AnalysisDetailPage tab recipe").
 *
 * Only the Overview tab is unconditional -- it is this same wave's own base route. Review
 * queue and Report are gated on FEATURES.projectReview / FEATURES.projectReports exactly
 * like every other cross-phase link in this client (ProjectLayout.js, ProjectDashboardPage.js):
 * Report is a sibling agent's page landing in this same wave, and a tab pointing at a route
 * App.js has not registered yet is precisely the dead control Anshul's 2026-09-06 standing
 * rule forbids. The orchestrator flips both flags once every page in the wave has landed.
 */
export default function VideoHeaderStrip({
  video,
  isLoading,
  isError,
  error,
  projectId,
  videoId,
  activeTab,
}) {
  const location = useLocation();
  const kindPill = KIND_PILLS[video?.kind] || null;
  const statusBadge = STATUS_BADGES[video?.status] || null;

  const tabs = [
    {
      id: 'overview',
      name: 'Overview',
      icon: HomeIcon,
      to: `/projects/${projectId}/videos/${videoId}`,
      shipped: true,
    },
    {
      id: 'review',
      name: 'Review Queue',
      icon: ClipboardDocumentCheckIcon,
      to: `/projects/${projectId}/videos/${videoId}/review`,
      shipped: FEATURES.projectReview,
    },
    {
      id: 'report',
      name: 'Report',
      icon: DocumentTextIcon,
      to: `/projects/${projectId}/videos/${videoId}/report`,
      shipped: FEATURES.projectReports,
    },
  ].filter((tab) => tab.shipped !== false);

  return (
    <div className="mb-6">
      {/* This and the tab links below pass location.state through, mirroring
          ProjectLayout.js's own nav buttons: a from-target set on the way into this
          project (main-app convention: location.state.from) must not get dropped the
          moment the user moves from Overview to Review queue to Report and back out -
          ProjectLayout's "Back to Projects" reads that same state three routes up. */}
      <Link
        to={`/projects/${projectId}/videos`}
        state={location.state}
        className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors mb-4"
      >
        <ArrowLeftIcon className="h-4 w-4 mr-1" />
        Back to videos &amp; photos
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center min-w-0">
          {/* A photo set is not a video (QA 2026-09-29: it showed the video camera). */}
          {video?.kind === 'photos' ? (
            <PhotoIcon className="h-8 w-8 text-blue-500 mr-3 flex-shrink-0" aria-hidden="true" />
          ) : (
            <VideoCameraIcon className="h-8 w-8 text-blue-500 mr-3 flex-shrink-0" aria-hidden="true" />
          )}
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-gray-900 truncate">
              {isLoading
                ? 'Loading…'
                : isError
                ? 'Video unavailable'
                : video?.title || 'Untitled video'}
            </h1>
            <div className="flex items-center flex-wrap gap-2 mt-1.5">
              {kindPill && (
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${kindPill.className}`}
                >
                  {kindPill.label}
                </span>
              )}
              {statusBadge && (
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.className}`}
                >
                  {video?.status === 'PROCESSING' && (
                    <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
                  )}
                  {statusBadge.label}
                </span>
              )}
              {/* A photo set has no duration to speak of (Anshul, 2026-09-26: "no
                  duration/fps tiles that would read as nonsense for photos") -- showing
                  "—" here would read as a stuck/broken video rather than an absent field. */}
              {video?.kind !== 'photos' && (
                <span className="text-sm text-gray-500">{formatDuration(video?.duration_s)}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {isError && (
        <p className="mb-4 text-sm text-red-600">
          {error?.response?.data?.detail || 'This video could not be loaded.'}
        </p>
      )}

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const current = activeTab === tab.id;
            return (
              <Link
                key={tab.id}
                to={tab.to}
                state={location.state}
                aria-current={current ? 'page' : undefined}
                className={`${
                  current
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
              >
                <Icon className="h-5 w-5" />
                <span>{tab.name}</span>
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
